import { browserLanguage, browserTimeZone } from "./siteLocale";

/** Countries where a seller can open a Stripe account (stripe.com/global). */
export const STRIPE_COUNTRIES = [
  "AE", "AT", "AU", "BE", "BG", "BR", "CA", "CH", "CY", "CZ", "DE", "DK", "EE", "ES", "FI", "FR", "GB", "GI", "GR", "HK", "HR", "HU", "IE", "IN", "IT", "JP",
  "LI", "LT", "LU", "LV", "MT", "MX", "MY", "NL", "NO", "NZ", "PL", "PT", "RO", "SE", "SG", "SI", "SK", "TH", "US",
] as const;

/** Currencies Stripe can charge in. The browser's own list also has retired ones (Slovenian tolar, Sudanese dinar). */
export const STRIPE_CURRENCIES = [
  "AED", "AFN", "ALL", "AMD", "AOA", "ARS", "AUD", "AWG", "AZN", "BAM", "BBD", "BDT", "BGN", "BIF", "BMD", "BND", "BOB", "BRL", "BSD", "BWP", "BYN", "BZD",
  "CAD", "CDF", "CHF", "CLP", "CNY", "COP", "CRC", "CVE", "CZK", "DJF", "DKK", "DOP", "DZD", "EGP", "ETB", "EUR", "FJD", "FKP", "GBP", "GEL", "GIP", "GMD",
  "GNF", "GTQ", "GYD", "HKD", "HNL", "HTG", "HUF", "IDR", "ILS", "INR", "ISK", "JMD", "JPY", "KES", "KGS", "KHR", "KMF", "KRW", "KYD", "KZT", "LAK", "LBP",
  "LKR", "LRD", "LSL", "MAD", "MDL", "MGA", "MKD", "MMK", "MNT", "MOP", "MUR", "MVR", "MWK", "MXN", "MYR", "MZN", "NAD", "NGN", "NIO", "NOK", "NPR", "NZD",
  "PAB", "PEN", "PGK", "PHP", "PKR", "PLN", "PYG", "QAR", "RON", "RSD", "RUB", "RWF", "SAR", "SBD", "SCR", "SEK", "SGD", "SHP", "SLE", "SOS", "SRD", "SZL",
  "THB", "TJS", "TOP", "TRY", "TTD", "TWD", "TZS", "UAH", "UGX", "USD", "UYU", "UZS", "VND", "VUV", "WST", "XAF", "XCD", "XOF", "XPF", "YER", "ZAR", "ZMW",
] as const;

/** The currency a seller in a Stripe country most likely sells in. */
const COUNTRY_CURRENCY: Record<string, string> = {
  AE: "AED", AU: "AUD", BG: "BGN", BR: "BRL", CA: "CAD", CH: "CHF", CZ: "CZK", DK: "DKK", GB: "GBP", GI: "GIP", HK: "HKD", HU: "HUF", IN: "INR", JP: "JPY",
  LI: "CHF", MX: "MXN", MY: "MYR", NO: "NOK", NZ: "NZD", PL: "PLN", RO: "RON", SE: "SEK", SG: "SGD", TH: "THB", US: "USD",
};
const EURO = ["AT", "BE", "CY", "DE", "EE", "ES", "FI", "FR", "GR", "HR", "IE", "IT", "LT", "LU", "LV", "MT", "NL", "PT", "SI", "SK"];
for (const code of EURO) COUNTRY_CURRENCY[code] = "EUR";

/** A time zone's country, for the zones of Stripe countries; the rest are left to the language. */
const ZONE_COUNTRY: Record<string, string> = {
  "Asia/Dubai": "AE", "Europe/Vienna": "AT", "Europe/Brussels": "BE", "Europe/Sofia": "BG", "Europe/Zurich": "CH", "Asia/Nicosia": "CY", "Europe/Nicosia": "CY",
  "Europe/Prague": "CZ", "Europe/Berlin": "DE", "Europe/Copenhagen": "DK", "Europe/Tallinn": "EE", "Europe/Madrid": "ES", "Atlantic/Canary": "ES",
  "Europe/Helsinki": "FI", "Europe/Paris": "FR", "Europe/London": "GB", "Europe/Gibraltar": "GI", "Europe/Athens": "GR", "Asia/Hong_Kong": "HK",
  "Europe/Zagreb": "HR", "Europe/Budapest": "HU", "Europe/Dublin": "IE", "Asia/Kolkata": "IN", "Asia/Calcutta": "IN", "Europe/Rome": "IT", "Asia/Tokyo": "JP",
  "Europe/Vaduz": "LI", "Europe/Vilnius": "LT", "Europe/Luxembourg": "LU", "Europe/Riga": "LV", "Europe/Malta": "MT", "America/Mexico_City": "MX",
  "America/Monterrey": "MX", "America/Tijuana": "MX", "America/Cancun": "MX", "Asia/Kuala_Lumpur": "MY", "Asia/Kuching": "MY", "Europe/Amsterdam": "NL",
  "Europe/Oslo": "NO", "Pacific/Auckland": "NZ", "Europe/Warsaw": "PL", "Europe/Lisbon": "PT", "Atlantic/Madeira": "PT", "Atlantic/Azores": "PT",
  "Europe/Bucharest": "RO", "Europe/Stockholm": "SE", "Asia/Singapore": "SG", "Europe/Ljubljana": "SI", "Europe/Bratislava": "SK", "Asia/Bangkok": "TH",
  "America/Sao_Paulo": "BR", "America/Toronto": "CA", "America/Vancouver": "CA", "America/Edmonton": "CA", "America/Winnipeg": "CA", "America/Halifax": "CA",
  "America/St_Johns": "CA", "America/Regina": "CA", "America/Montreal": "CA", "America/New_York": "US", "America/Chicago": "US", "America/Denver": "US",
  "America/Phoenix": "US", "America/Los_Angeles": "US", "America/Anchorage": "US", "America/Detroit": "US", "Pacific/Honolulu": "US",
};

/**
 * The seller's likely country: from the site's time zone (set in Site details), else the browser's, else the browser
 * language's region ("en-SG"). Null when none of them names a Stripe country.
 */
export function likelyCountry(siteTimeZone?: string | null): string | null {
  const stripe = new Set<string>(STRIPE_COUNTRIES);
  for (const zone of [siteTimeZone, browserTimeZone()]) {
    if (!zone) continue;
    const country = ZONE_COUNTRY[zone] ?? (zone.startsWith("Australia/") ? "AU" : undefined);
    if (country) return country;
  }
  const region = browserLanguage()?.split("-").find((part, index) => index > 0 && /^[A-Z]{2}$/.test(part));
  return region && stripe.has(region) ? region : null;
}

/** The currency a new shop starts with: the likely country's, else US dollars. */
export function likelyCurrency(siteTimeZone?: string | null): string {
  const country = likelyCountry(siteTimeZone);
  return (country && COUNTRY_CURRENCY[country]) || "USD";
}

const currencyNames = new Intl.DisplayNames(["en"], { type: "currency" });
const countryNames = new Intl.DisplayNames(["en"], { type: "region" });

/** Currency choices by name, "Singapore Dollar · SGD"; a saved code outside the list still shows. */
export function currencyOptions(current?: string | null): Array<{ value: string; label: string; keywords: string }> {
  const codes = new Set<string>(STRIPE_CURRENCIES);
  if (current) codes.add(current);
  return [...codes].map((code) => ({ value: code, label: `${currencyNames.of(code) ?? code} · ${code}`, keywords: code })).sort((a, b) => a.label.localeCompare(b.label));
}

/** Stripe country choices by name. */
export function stripeCountryOptions(): Array<{ value: string; label: string; keywords: string }> {
  return STRIPE_COUNTRIES.map((code) => ({ value: code, label: countryNames.of(code) ?? code, keywords: code })).sort((a, b) => a.label.localeCompare(b.label));
}
